import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue, errDateMap, filterUser, filterUsers, getSelectValues, setValues} from "@/helper/method";
import {useRoleUsers} from "@/store/user.c";
import {RoleUserVO} from "@/service/user.s";
import {isNumber} from "lodash";
import {ShowChartOutlined} from '@mui/icons-material';
import {useCreatePlan, useQueryPlan, useUpdatePlan} from "@/service/plan.s";
import {PlanPO} from "@/model/mission";
import {TagOut} from "@/widget/TagOut/TagOut";

const validationSchema = z.object({
    id: z.any().nullable(),
    planName: z.string({message: "请填写目标名称"}),
    executor: z.ostring(),
    status: z.ostring(),
    statusId: z.number({message: "请选目标状态"}),
    description: z.ostring(),
    ratio: z.number({message: "请填写目标进度"}).max(100, "目标进度不能大于100"),
    executorId: z.number({message: "请选择负责人"}),
    startTime: z.coerce.date({
        errorMap: errDateMap("请选择预计开始时间")
    }),
    endTime: z.coerce.date({
        errorMap: errDateMap("请选择预计结束时间")
    }),
    owners: z.ostring(),
    ownerIds: z.string({message: "请选择目标成员"}),
    tempOwners: z.array(z.object({username: z.string(), id: z.number()})).optional(),
    tempExecutor: z.object({username: z.string().default(''), id: z.number()}),
});

export type WritePlanForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WritePlan = () => {
    const allDict = useAllDict()
    const allUser = useRoleUsers([])
    const {planStatus} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createPlanMutate, isPending: createPlanPending} = useCreatePlan()
    const {mutateAsync: updatePlanMutate, isPending: updatePlanPending} = useUpdatePlan()
    const {onSearch, write, id: drawerId} = useDrawerModal()
    const {data: planData} = useQueryPlan({id: drawerId!}, isNumber(drawerId) && write == 'edit')
    const plan = planData?.data || {} as PlanPO
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {...plan, tempOwners: filterUsers(allUser, plan?.ownerIds), tempExecutor: filterUser(allUser, plan?.executorId)}
        }
        return {}
    }
    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WritePlanForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WritePlanForm,
        defaultValues,
    });

    const onSubmit = async (values: WritePlanForm) => {
        const result = write == "edit" ? await updatePlanMutate({...values, id: drawerId}) : await createPlanMutate(values)
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>目标名称</FormLabel>
                        <TextField
                            error={!!errors.planName}
                            helperText={errors?.planName?.message}
                            {...register("planName")}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 140},
                                },
                            }}
                            placeholder="填写目标名称"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>负责人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                options={allUser}
                                value={value}
                                onChange={(_, value: RoleUserVO | null) => {
                                    setValue("executorId", value!.id)
                                    setValue("executor", value?.username)
                                    onChange(value)
                                }}
                                isOptionEqualToValue={(option, value) => option.id == value.id}
                                getOptionKey={option => option.username}
                                getOptionLabel={option => option.username}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.executorId}
                                               helperText={errors?.executorId?.message}
                                               variant="outlined"
                                               placeholder={"选择负责人"} {...params} />
                                )}
                            />
                        }} name={"tempExecutor"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="startTime">预计开始时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择预计开始时间"}
                                customInput={<TextField
                                    error={!!errors?.startTime}
                                    helperText={errors?.startTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"startTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel>预计结束时间</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                            <DatePicker
                                showYearDropdown={true}
                                showMonthDropdown={true}
                                dateFormat="yyyy/MM/dd"
                                selected={value}
                                onChange={date => onChange(date)}
                                id='account-settings-date'
                                locale='zhCN'
                                placeholderText={"选择预计结束时间"}
                                customInput={<TextField
                                    error={!!errors?.endTime}
                                    helperText={errors?.endTime?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                />}
                            />
                        </DatePickers>} name={"endTime"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>目标成员</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                slotProps={{chip: {size: 'small',}}}
                                options={allUser}
                                value={value}
                                disableListWrap={true}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "username", val: 'id'}, {key: "ownerIds", val: "owners"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                renderTags={(values) => {
                                    return values?.map((value: RoleUserVO, index: number) => <TagOut index={index} label={value.username}/>)
                                }}
                                isOptionEqualToValue={(option, value) => option.username === value.username}
                                getOptionLabel={(option: RoleUserVO) => option.username}
                                getOptionKey={(option) => option.username}
                                disableCloseOnSelect={true}
                                renderInput={(params) => (
                                    <TextField error={!!errors?.ownerIds}
                                               sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                               helperText={errors?.ownerIds?.message} placeholder={"选择目标成员"} {...params} size={'small'}/>
                                )}
                            />
                        }} name={"tempOwners"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>目标状态</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                            options={planStatus}
                            fullWidth
                            value={value as any}
                            onChange={(_, option: DictPO | null) => {
                                setValue("statusId", option!.value)
                                onChange(option?.label)
                            }}
                            renderInput={(params) => (
                                <TextField error={!!errors?.statusId}
                                           helperText={errors?.statusId?.message}
                                           variant="outlined"
                                           placeholder={"选择目标状态"} {...params} />
                            )}
                        />} name={"status"}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} autoFocus={true} required={true}>
                        <FormLabel htmlFor="ratio">目标进度</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => <TextField
                            error={!!errors?.ratio}
                            helperText={errors?.ratio?.message}
                            id="ratio"
                            value={value}
                            onChange={event => onChange(parseInt(event.target.value) || '')}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: 0,
                                        max: 1000,
                                        maxLength: 3, inputMode: 'numeric', pattern: '[0-9]*'
                                    },
                                    endAdornment: <ShowChartOutlined color="disabled"/>,
                                },
                            }}
                            placeholder="填写目标进度"
                            variant="outlined"
                        />} name={'ratio'}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="description">目标描述</FormLabel>
                        <TextField
                            id="description"
                            multiline
                            rows={3}
                            {...register("description")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto', minHeight: 80},
                                },
                            }}
                            placeholder="目标描述"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createPlanPending || updatePlanPending} onReset={() => reset(defaultValues)}/>
        </Box>
    )
        ;
};
